import React from 'react';
import { Check, ExternalLink } from 'lucide-react';

const SOURCE_LABELS = { icecat: 'Icecat', ldlc: 'LDLC', darty: 'Darty', fnac: 'Fnac', boulanger: 'Boulanger', materiel: 'Materiel.net', leclerc: 'Leclerc', topachat: 'TopAchat', cdiscount: 'Cdiscount', dell: 'Dell', hp: 'HP', lenovo: 'Lenovo', asus: 'ASUS', acer: 'Acer', apple: 'Apple', samsung: 'Samsung', microsoft: 'Microsoft', firecrawl: 'Web', autre: 'Autre' };
const SOURCE_COLORS = { icecat: 'blue', ldlc: 'green', darty: 'red', fnac: 'orange', boulanger: 'amber', materiel: 'cyan', leclerc: 'emerald', topachat: 'violet', cdiscount: 'pink', dell: 'blue', hp: 'sky', lenovo: 'red', asus: 'indigo', acer: 'lime', apple: 'gray', samsung: 'blue', microsoft: 'cyan', firecrawl: 'gray', autre: 'gray' };

const DEFAULT_FIELDS = [
  { key: 'name', label: 'Nom' },
  { key: 'processor', label: 'CPU' },
  { key: 'ram', label: 'RAM' },
  { key: 'storage', label: 'Stockage' },
  { key: 'screen_size', label: 'Ecran' },
  { key: 'price', label: 'Prix' },
  { key: 'description', label: 'Description' },
];

export default function SourceComparisonTable({ sources, selectedValues, onValueChange, fields = DEFAULT_FIELDS }) {
  const foundSources = Object.entries(sources).filter(([, s]) => s?.found);

  const handleSourceClick = (field, value) => {
    if (!value) return;
    const current = selectedValues[field] || '';
    if (field === 'price' || field === 'name') {
      onValueChange(field, value);
      return;
    }
    const parts = current.split(' — ').map(p => p.trim()).filter(Boolean);
    const idx = parts.findIndex(p => p.toLowerCase() === value.toLowerCase());
    if (idx >= 0) {
      parts.splice(idx, 1);
    } else {
      parts.push(value);
    }
    onValueChange(field, parts.join(' — '));
  };

  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      <div className="flex flex-wrap gap-2 px-4 pt-4 pb-2">
        {Object.entries(sources).map(([key, src]) => (
          <div key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
            src?.found
              ? `bg-${SOURCE_COLORS[key]}-500/20 text-${SOURCE_COLORS[key]}-400`
              : 'bg-slate-100 text-slate-500'
          }`}>
            {src?.found ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 text-center">✕</span>}
            {src?.source_label || SOURCE_LABELS[key] || key}
            {src?.source_url && (
              <a href={src.source_url} target="_blank" rel="noopener noreferrer"
                className="ml-1 opacity-60 hover:opacity-100">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left text-xs text-slate-500 font-normal px-4 py-2 w-28">Champ</th>
            {foundSources.map(([key]) => (
              <th key={key} className="text-left text-xs text-slate-600 font-medium px-4 py-2">
                {SOURCE_LABELS[key]}
              </th>
            ))}
            <th className="text-left text-xs text-secondary font-medium px-4 py-2">Selection finale</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(({ key, label }) => {
            const values = foundSources.map(([, s]) => s[key] || '').filter(Boolean);
            const allSame = new Set(values.map(v => v.toString().toLowerCase().trim())).size <= 1;
            const isTextarea = key === 'description';

            return (
              <tr key={key} className="border-b border-slate-200 last:border-0">
                <td className="px-4 py-2.5 text-xs text-slate-500">{label}</td>
                {foundSources.map(([sKey, s]) => {
                  const v = s[key] || '';
                  const currentVal = selectedValues[key] || '';
                  const isIncluded = v && currentVal.toLowerCase().includes(v.toString().toLowerCase());
                  const canCumulate = key !== 'price' && key !== 'name';
                  return (
                    <td key={sKey} className="px-4 py-2.5">
                      {v ? (
                        <button type="button" onClick={() => handleSourceClick(key, v)}
                          className={`text-left text-xs px-2 py-1 rounded-sm transition-colors w-full flex items-center gap-1.5 ${
                            isIncluded
                              ? 'bg-secondary/15 text-secondary border border-secondary/40'
                              : allSame ? 'text-slate-700 hover:bg-slate-100' : 'text-yellow-300 hover:bg-yellow-500/10 border border-yellow-500/20'
                          }`}>
                          {canCumulate && (
                            <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${isIncluded ? 'bg-secondary border-secondary' : 'border-slate-300'}`}>
                              {isIncluded && <Check className="w-2.5 h-2.5 text-black" />}
                            </span>
                          )}
                          <span className="flex-1">{v}</span>
                        </button>
                      ) : (
                        <span className="text-xs text-gray-600">-</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-2.5">
                  {isTextarea ? (
                    <textarea
                      value={selectedValues[key] || ''}
                      onChange={e => onValueChange(key, e.target.value)}
                      rows={2}
                      className="w-full bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5 text-xs text-white outline-none resize-none focus:border-secondary/50"
                    />
                  ) : (
                    <input
                      type={key === 'price' ? 'number' : 'text'}
                      step={key === 'price' ? '0.01' : undefined}
                      value={selectedValues[key] || ''}
                      onChange={e => onValueChange(key, e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5 text-xs text-white outline-none focus:border-secondary/50"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[10px] text-gray-600 border-t border-slate-200">
        Cliquez a gauche pour pre-remplir · Editez directement a droite pour ajuster
      </div>
    </div>
  );
}
